import { useAtomValue } from 'jotai';
import { useMemo } from 'react';
import { useGetRegistries } from '~/api/registries';
import { userConfig } from '~/global-state/userConfig';
import type { RegistryIntegrationMode } from '~/schemas/organizations';

export interface PushEnabledRegistry {
  identifier: string;
  label: string;
  mode: Exclude<RegistryIntegrationMode, 'disabled'>;
  supportedActionTypes: string[];
}

/**
 * Registries that can be pushed to for the current org: the registry supports push AND the org has
 * enabled it (semi- or fully-automatic). Drives whether the push option is offered and which registry
 * the user can select. `isPending` is true until the registries have loaded — the list is empty then,
 * so callers must not treat it as "no push registries" yet.
 */
export function usePushEnabledRegistries(): { pushEnabledRegistries: PushEnabledRegistry[]; isPending: boolean } {
  const { registryIntegration } = useAtomValue(userConfig);
  const { data: registries = [], isPending } = useGetRegistries();

  const pushEnabledRegistries = useMemo(
    () =>
      registries
        .filter((registry) => registry.supports_registry_action_push)
        .map((registry) => ({
          identifier: registry.identifier,
          label: registry.label,
          mode: registryIntegration[registry.identifier],
          supportedActionTypes: registry.supported_push_action_types ?? [],
        }))
        .filter((registry): registry is PushEnabledRegistry => !!registry.mode && registry.mode !== 'disabled'),
    [registries, registryIntegration],
  );

  return { pushEnabledRegistries, isPending };
}
